import { useState, useEffect } from 'react';
import { useChartColors } from '@/hooks/useChartColors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { AlertTriangle, Clock, DollarSign, Download, Users } from 'lucide-react';
import { format, differenceInHours } from 'date-fns';

interface HospitalizationEvent {
  id: string;
  patient_id: string;
  event_type: string;
  event_timestamp: string;
  facility_name: string | null;
  admission_reason: string | null;
}

interface Patient {
  id: string;
  first_name: string;
  last_name: string;
  hospital_er_hours: number | null;
}

interface PatientInterruption {
  patientId: string;
  patientName: string;
  facilityName: string;
  admissionReason: string;
  admittedAt: string;
  hoursLost: number;
  revenueLost: number;
  isOngoing: boolean;
}

interface BillingInterruptionReportProps {
  agencyId: string;
}

export function BillingInterruptionReport({ agencyId }: BillingInterruptionReportProps) {
  const chartColors = useChartColors();
  const [loading, setLoading] = useState(true);
  const [interruptions, setInterruptions] = useState<PatientInterruption[]>([]);
  const [stateBillingRate, setStateBillingRate] = useState<number>(0);
  const [totals, setTotals] = useState({
    totalPatientsAffected: 0,
    currentlyAdmitted: 0,
    totalHoursLost: 0,
    totalRevenueLost: 0,
  });

  useEffect(() => {
    loadData();
  }, [agencyId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Fetch agency billing rate
      const { data: agencyData } = await supabase
        .from('agencies')
        .select('state_billing_rate')
        .eq('id', agencyId)
        .single();

      const billingRate = agencyData?.state_billing_rate || 0;
      setStateBillingRate(billingRate);

      // Fetch all hospitalization events for the agency
      const { data: events } = await supabase
        .from('patient_hospitalization_events')
        .select('*')
        .eq('agency_id', agencyId)
        .order('event_timestamp', { ascending: true });

      // Fetch all patients for the agency
      const { data: patients } = await supabase
        .from('patients')
        .select('id, first_name, last_name, hospital_er_hours')
        .eq('agency_id', agencyId);

      if (!events || !patients) {
        setLoading(false);
        return;
      }

      // Create patient lookup
      const patientLookup = new Map<string, Patient>();
      patients.forEach(p => patientLookup.set(p.id, p));

      // Group events by patient
      const patientEvents = new Map<string, HospitalizationEvent[]>();
      events.forEach(event => {
        const existing = patientEvents.get(event.patient_id) || [];
        existing.push(event);
        patientEvents.set(event.patient_id, existing);
      });

      // Calculate interruptions
      const interruptionsList: PatientInterruption[] = [];
      let currentlyAdmitted = 0;
      let totalHoursLost = 0;

      patientEvents.forEach((patientEventList, patientId) => {
        const patient = patientLookup.get(patientId);
        if (!patient) return;

        // Process admission/discharge pairs
        let currentAdmission: HospitalizationEvent | null = null;

        for (const event of patientEventList) {
          if (event.event_type === 'admitted') {
            currentAdmission = event;
          } else if (event.event_type === 'discharged' && currentAdmission) {
            // Calculate hours for completed stay
            const admitTime = new Date(currentAdmission.event_timestamp);
            const dischargeTime = new Date(event.event_timestamp);
            const hoursLost = differenceInHours(dischargeTime, admitTime);
            const revenueLost = hoursLost * billingRate;

            interruptionsList.push({
              patientId,
              patientName: `${patient.first_name} ${patient.last_name}`,
              facilityName: currentAdmission.facility_name || 'Unknown Facility',
              admissionReason: currentAdmission.admission_reason || 'Not specified',
              admittedAt: currentAdmission.event_timestamp,
              hoursLost,
              revenueLost,
              isOngoing: false,
            });

            totalHoursLost += hoursLost;
            currentAdmission = null;
          }
        }

        // Check for ongoing admission
        if (currentAdmission) {
          const admitTime = new Date(currentAdmission.event_timestamp);
          const hoursLost = differenceInHours(new Date(), admitTime);
          const revenueLost = hoursLost * billingRate;

          interruptionsList.push({
            patientId,
            patientName: `${patient.first_name} ${patient.last_name}`,
            facilityName: currentAdmission.facility_name || 'Unknown Facility',
            admissionReason: currentAdmission.admission_reason || 'Not specified',
            admittedAt: currentAdmission.event_timestamp,
            hoursLost,
            revenueLost,
            isOngoing: true,
          });

          totalHoursLost += hoursLost;
          currentlyAdmitted++;
        }
      });

      // Sort by most recent first
      interruptionsList.sort((a, b) => 
        new Date(b.admittedAt).getTime() - new Date(a.admittedAt).getTime()
      );

      const totalRevenueLost = totalHoursLost * billingRate;
      const uniquePatients = new Set(interruptionsList.map(i => i.patientId)).size;

      setInterruptions(interruptionsList);
      setTotals({
        totalPatientsAffected: uniquePatients,
        currentlyAdmitted,
        totalHoursLost,
        totalRevenueLost,
      });
    } catch (error) {
      console.error('Error loading billing interruption data:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const headers = [
      'Patient Name',
      'Facility',
      'Admission Reason',
      'Admitted At',
      'Hours Lost',
      'Revenue Lost ($)',
      'Status',
    ];

    const rows = interruptions.map(i => [
      i.patientName,
      i.facilityName,
      i.admissionReason,
      format(new Date(i.admittedAt), 'yyyy-MM-dd HH:mm'),
      i.hoursLost.toString(),
      i.revenueLost.toFixed(2),
      i.isOngoing ? 'Currently Admitted' : 'Discharged',
    ]);

    // Add summary rows
    rows.push([]);
    rows.push(['Summary']);
    rows.push(['Total Patients Affected', totals.totalPatientsAffected.toString()]);
    rows.push(['Currently Admitted', totals.currentlyAdmitted.toString()]);
    rows.push(['Total Hours Lost', totals.totalHoursLost.toString()]);
    rows.push(['Total Revenue Lost ($)', totals.totalRevenueLost.toFixed(2)]);
    rows.push(['State Billing Rate ($/hr)', stateBillingRate.toFixed(2)]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(',')),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `billing-interruption-report-${format(new Date(), 'yyyy-MM-dd')}.csv`;
    link.click();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <Card className={`${chartColors.cardClassName} shadow-sm`}>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-72 mt-2" />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[1, 2, 3, 4].map(i => (
              <Skeleton key={i} className="h-24" />
            ))}
          </div>
          <Skeleton className="h-64" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${chartColors.cardClassName} shadow-sm`}>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Billing Interruption Report
          </CardTitle>
          <CardDescription>
            Revenue impact from patient hospitalizations (Rate: {formatCurrency(stateBillingRate)}/hr)
          </CardDescription>
        </div>
        <Button variant="outline" size="sm" onClick={exportToCSV}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </CardHeader>
      <CardContent>
        {/* Summary KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-muted/50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Patients Affected</span>
              </div>
              <p className="text-2xl font-bold mt-1">{totals.totalPatientsAffected}</p>
            </CardContent>
          </Card>

          <Card className="bg-destructive/10 border-destructive/20">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <span className="text-sm text-muted-foreground">Currently Admitted</span>
              </div>
              <p className="text-2xl font-bold mt-1 text-destructive">{totals.currentlyAdmitted}</p>
            </CardContent>
          </Card>

          <Card className="bg-muted/50">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Total Hours Lost</span>
              </div>
              <p className="text-2xl font-bold mt-1">{totals.totalHoursLost.toLocaleString()}</p>
            </CardContent>
          </Card>

          <Card className="bg-destructive/10 border-destructive/20">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-destructive" />
                <span className="text-sm text-muted-foreground">Total Revenue Lost</span>
              </div>
              <p className="text-2xl font-bold mt-1 text-destructive">
                {formatCurrency(totals.totalRevenueLost)}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Table */}
        {interruptions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <AlertTriangle className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>No hospitalization events recorded</p>
          </div>
        ) : (
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient</TableHead>
                  <TableHead>Facility</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Admitted</TableHead>
                  <TableHead className="text-right">Hours Lost</TableHead>
                  <TableHead className="text-right">Revenue Lost</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {interruptions.map((interruption, index) => (
                  <TableRow key={`${interruption.patientId}-${index}`}>
                    <TableCell className="font-medium">{interruption.patientName}</TableCell>
                    <TableCell>{interruption.facilityName}</TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {interruption.admissionReason}
                    </TableCell>
                    <TableCell>
                      {format(new Date(interruption.admittedAt), 'MMM d, yyyy h:mm a')}
                    </TableCell>
                    <TableCell className="text-right">{interruption.hoursLost}</TableCell>
                    <TableCell className="text-right font-medium text-destructive">
                      {formatCurrency(interruption.revenueLost)}
                    </TableCell>
                    <TableCell>
                      {interruption.isOngoing ? (
                        <Badge variant="destructive">Admitted</Badge>
                      ) : (
                        <Badge variant="secondary">Discharged</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
